/**
 * The phone a member signs in with — the pure rules, so `node --test` pins them (signInPhone.test.js).
 * Plan: vault "Phone Numbers for Sign-In and Profile Claiming — Phase One Build Plan".
 *
 *   toE164           what a person typed → "+14155550100", or null. US and Canadian numbers only:
 *                    Twilio is set to text those two countries and nowhere else.
 *   formatPhone      a stored number → "(415) 555-0100", the way a person reads it back.
 *   samePhone        whether two numbers, however typed, are the same phone.
 *   maskPhone        "•••-7495", for a door that shows whose phone a code would go to.
 *   phoneSuggestions the numbers a person has already given — on their Home contacts or as a profile's
 *                    Phone answer — offered as "Use … from your profile", minus the one in use.
 *   phoneCodeMessage what to say when a step fails, from the auth server's error.
 *   isUnknownPhone   whether signing in failed because no account has the number — the one failure
 *                    the sign-in form turns into "add your phone", not an error.
 *
 * Supabase stores a confirmed number as bare digits ("14155550100") and takes it with or without the
 * plus, so every comparison goes through toE164 first.
 */

// A North American number: area code and exchange each start 2–9.
const NANP = /^[2-9]\d{2}[2-9]\d{6}$/;

export function toE164(input) {
  if (input == null) return null;
  const raw = String(input).trim().replace(/^tel:/i, '');
  if (!raw) return null;
  let digits = raw.replace(/\D/g, '');
  // A leading + with any country but 1 is a number we do not text.
  if (raw.startsWith('+') && !digits.startsWith('1')) return null;
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (!NANP.test(digits)) return null;
  return `+1${digits}`;
}

export function formatPhone(value) {
  const e164 = toE164(value);
  if (!e164) return value ? String(value) : '';
  const d = e164.slice(2);
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** "•••-7495": enough of a number for its owner to recognise, and no more. '' when it is not one. */
export function maskPhone(value) {
  const e164 = toE164(value);
  return e164 ? `•••-${e164.slice(-4)}` : '';
}

export function samePhone(a, b) {
  const x = toE164(a);
  return !!x && x === toE164(b);
}

/**
 * @param {{ homeLinks?: string[], answers?: string[], current?: string|null }} p
 *   homeLinks: `tel:` URLs from the Home "Contact and links"; answers: Phone answers the person gave
 * @returns {string[]}  E.164 numbers, Home contacts first, each once, never the one already in use
 */
export function phoneSuggestions({ homeLinks = [], answers = [], current = null } = {}) {
  const out = [];
  for (const v of [...homeLinks, ...answers]) {
    const n = toE164(v);
    if (n && !out.includes(n) && !samePhone(n, current)) out.push(n);
  }
  return out;
}

/** No account has this number: Supabase refuses to make one (`shouldCreateUser: false`). */
export function isUnknownPhone(err) {
  return err?.code === 'otp_disabled' || /signups? not allowed|user not found/i.test(String(err?.message || ''));
}

/**
 * @param {Error|{ message?: string, code?: string }} err
 * @param {'add'|'signin'} step  adding a phone to an account, or signing in with one
 */
export function phoneCodeMessage(err, step = 'add') {
  const code = err?.code || '';
  const msg = String(err?.message || '');
  if (code === 'phone_exists' || /phone number has already been registered/i.test(msg)) return 'That number is on another account.';
  if (code === 'otp_expired' || /expired or is invalid|invalid.*(otp|token|code)/i.test(msg)) return 'That code isn’t right, or it has expired.';
  if (step === 'signin' && isUnknownPhone(err)) {
    return 'We don’t have that number. Sign in with your email, then add your phone.';
  }
  if (/security purposes|rate.?limit|too many|over_sms_send_rate_limit/i.test(`${code} ${msg}`)) return 'Please wait a moment before asking for another code.';
  if (/region|country|geo|21408|60220|permission to send/i.test(msg)) return 'We can only text numbers in the US and Canada.';
  return step === 'signin'
    ? 'We couldn’t text that number. Try again, or sign in with your email.'
    : 'We couldn’t text that number. Try again in a moment.';
}
